/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import { type ReactNode, useCallback } from 'react';
import {
  documentToReactComponents,
  type Options
} from '@contentful/rich-text-react-renderer';
import { BLOCKS, MARKS } from '@contentful/rich-text-types';
import { rgba } from 'polished';
import sanitizeHtml from 'sanitize-html';
import styled from 'styled-components';

import * as FOD from '@/components/blog/friends-of-data';
import { useIsHydrated } from '@/utils/ClientSuspense';

import { BlogHint } from '../blog/BlogHint';
import { BlogQuote } from '../blog/BlogQuote';
import { FloatImage } from '../blog/FloatImage';
import { InlineImage } from '../blog/InlineImage';
import { InlineVideo } from '../blog/InlineVideo';
import { LoomVideo } from '../blog/LoomVideo';
import { flattenNodeText, textToId } from '../blog/utils';
import { Callout, Heading, Text } from '../design-system';

import { HexEmbed } from './HexEmbed';
import { INVESTOR_WHEEL_TEXT, InvestorWheel } from './InvestorWheel';

export const richTextParagraph = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node: any, children: ReactNode) => (
      <Text>{children}</Text>
    )
  }
};

export const richTextMedium = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node: any, children: ReactNode) => (
      <Text size="medium">{children}</Text>
    )
  }
};

export const richTextLarge = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node: any, children: ReactNode) => (
      <Text size="large">{children}</Text>
    )
  }
};

const Wrapper = styled.div`
  color: ${({ theme }) => theme.fontColor.LOUD};
`;

const StyledText = styled(Text)`
  color: ${({ theme }) => theme.fontColor.LOUD};
`;

const TableWrapper = styled.div`
  display: flex;
  justify-content: center;

  @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
    overflow-x: auto;
    justify-content: unset;
  }
`;

const StyledTable = styled.table`
  min-width: 100%;
  border-collapse: collapse;
  margin-bottom: 25px;
`;

const StyledHeader = styled.th`
  &&&& {
    background: ${({ theme }) => rgba(theme.highlightColor, 0.1)};
    border: 1px solid ${({ theme }) => theme.borderColor.DEFAULT};

    text-align: center;
    padding: 10px 30px;

    > p {
      margin-bottom: 0;
      font-family: ${({ theme }) => theme.fontFamily.MONO};
      font-size: ${({ theme }) => theme.fontSize.SMALL};
      line-height: ${({ theme }) => theme.lineHeight.SMALL};
      letter-spacing: 1px;
      text-transform: uppercase;

      > b {
        color: ${({ theme }) => theme.fontColor.DEFAULT};
        font-weight: ${({ theme }) => theme.fontWeight.NORMAL};
        font-size: ${({ theme }) => theme.fontSize.DEFAULT};
      }
    }
  }
`;

const StyledRow = styled.tr`
  border-bottom: 1px solid ${({ theme }) => theme.borderColor.DEFAULT};

  &:last-child {
    border-bottom: 1px solid ${({ theme }) => theme.borderColor.DEFAULT};
  }

  & > td {
    border-left: 1px solid ${({ theme }) => theme.borderColor.DEFAULT};
    border-right: 1px solid ${({ theme }) => theme.borderColor.DEFAULT};
  }
`;

const StyledCell = styled.td`
  &&&& {
    text-align: center;
    padding: 10px 30px;

    > p {
      margin-bottom: 0;
      font-family: ${({ theme }) => theme.fontFamily.MONO};
      font-size: ${({ theme }) => theme.fontSize.SMALL};
      line-height: ${({ theme }) => theme.lineHeight.SMALL};
      letter-spacing: 1px;
      text-transform: uppercase;
      color: ${({ theme }) => theme.fontColor.DEFAULT};
      font-weight: ${({ theme }) => theme.fontWeight.NORMAL};
    }
  }
`;

interface RichTextProps {
  body?: {
    raw: string;
    references?: {
      filter(arg0: (ref: any) => boolean): any;
    };
  };
  renderNodeOverrides?: Options['renderNode'];
}

export const RichText: React.ComponentType<RichTextProps> = React.memo(
  ({ body, renderNodeOverrides }) => {
    const json = body ? JSON.parse(body.raw) : null;
    const isHydrated = useIsHydrated();

    const findAsset = useCallback(
      (id: string) => {
        const references = body?.references || [];
        return references.filter(ref => ref.contentful_id === id);
      },
      [body]
    );

    const renderOptions: Options = {
      renderMark: {
        [MARKS.CODE]: (text: ReactNode) => {
          let language = null;
          let code = text;

          if (typeof text === 'string') {
            const codeLines = text.split('\n');
            const hasPragma = codeLines?.[0].includes('@language-');

            if (hasPragma) {
              const pragma = codeLines?.[0];
              const pragmaLng = pragma?.split('@language-')?.[1];

              if (pragmaLng) {
                language = pragmaLng;
                code = codeLines.slice(1, codeLines.length).join('\n');
              }
            }
          }

          return (
            <code className={language ? `language-${language} ` : ''}>
              {code}
            </code>
          );
        }
      },
      renderText: (text: string) => {
        if (text.includes('www.loom.com')) {
          return <LoomVideo aspectratio="60.742407199100114%" src={text} />;
        } else if (text.includes(INVESTOR_WHEEL_TEXT)) {
          return <InvestorWheel text={text} />;
        } else {
          return text;
        }
      },
      renderNode: {
        [BLOCKS.PARAGRAPH]: (node: any, children: ReactNode) => {
          // this if statement is checking if the code text is in its own paragraph block
          // because that means it's a stand alone code snippet, instead of inline code
          if (
            node.content.every(
              // @ts-expect-error nodes aren't typed atm
              child =>
                Array.isArray(child.marks) &&
                // @ts-expect-error nodes aren't typed atm
                child.marks.map(m => m.type).includes('code')
            )
          ) {
            return <pre>{children}</pre>;
          }

          return <StyledText size="medium">{children}</StyledText>;
        },
        [BLOCKS.HEADING_2]: (node: any, children: ReactNode) => {
          const id = textToId(flattenNodeText(node));

          return (
            <Heading id={id} renderAs="h2">
              {children}
            </Heading>
          );
        },
        [BLOCKS.HEADING_3]: (node: any, children: ReactNode) => (
          <Heading renderAs="h3">{children}</Heading>
        ),
        [BLOCKS.TABLE]: (node: any, children: ReactNode) => (
          <TableWrapper>
            <StyledTable>
              <tbody>{children}</tbody>
            </StyledTable>
          </TableWrapper>
        ),
        [BLOCKS.TABLE_HEADER_CELL]: (node, children) => (
          <StyledHeader>{children}</StyledHeader>
        ),
        [BLOCKS.TABLE_ROW]: (node, children) => (
          <StyledRow>{children}</StyledRow>
        ),
        [BLOCKS.TABLE_CELL]: (node, children) => (
          <StyledCell>{children}</StyledCell>
        ),
        // embedded entries are other contentful instances that are embedded in the blog post
        // this is separate from media
        [BLOCKS.EMBEDDED_ENTRY]: (node: any) => {
          const asset = findAsset(node.data.target.sys.id)[0];
          // Handle the case when asset is not found
          if (!asset) {
            return;
          }
          //Handle the case when asset is an image but the URL is not present
          if (asset.image && typeof asset.image.url !== 'string') {
            return null;
          }
          const whatToSwitch = asset.__typename || asset.internal.type;
          switch (whatToSwitch) {
            case 'ContentfulBlogHexEmbed': {
              const { caption, height, source } = asset;
              return (
                <HexEmbed caption={caption} height={height} src={source} />
              );
            }
            case 'ContentfulBlogHint': {
              const blogHint = JSON.parse(asset.body.raw);
              return (
                <BlogHint children={documentToReactComponents(blogHint)} />
              );
            }
            case 'ContentfulBlogCallout': {
              const callout = JSON.parse(asset.body.raw);
              return <Callout children={documentToReactComponents(callout)} />;
            }
            case 'ContentfulBlogQuote': {
              const text = asset.body.body;
              return <BlogQuote> {text}</BlogQuote>;
            }
            case 'ContentfulBlogFloatImage':
              if (!asset.image) {
                return null;
              }
              return (
                <FloatImage
                  offset={asset.offset}
                  position={asset.position.toLowerCase()}
                  src={asset.image.url}
                />
              );
            case 'ContentfulBlogLoomVideo':
              return (
                <LoomVideo aspectratio="60.742407199100114%" src={asset.url} />
              );
            case 'ContentfulBlogHtml':
              if (isHydrated) {
                const rawHtml = sanitizeHtml(asset.markup.markup, {
                  allowedTags: sanitizeHtml.defaults.allowedTags.concat([
                    'iframe'
                  ]),
                  allowedAttributes: {
                    iframe: [
                      'src',
                      'style',
                      'width',
                      'height',
                      'frameborder',
                      'allowfullscreen',
                      {
                        name: 'allow',
                        multiple: true,
                        values: [
                          'autoplay;',
                          'clipboard-write;',
                          'encrypted-media;',
                          'fullscreen;',
                          'picture-in-picture;'
                        ]
                      },
                      'loading'
                    ]
                  },
                  allowedIframeHostnames: ['open.spotify.com']
                });
                return <div dangerouslySetInnerHTML={{ __html: rawHtml }} />;
              } else {
                return null;
              }

            case 'ContentfulAudioSnippet':
              return (
                <FOD.AudioSnippet
                  end={asset.endTimeStamp}
                  src={asset.audioFile.url}
                  start={asset.startTimeStamp}
                />
              );

            default:
              return null;
          }
        },
        // embedded assets are media, like images, videos, and gifs
        [BLOCKS.EMBEDDED_ASSET]: (node: any) => {
          const asset = findAsset(node.data.target.sys.id)[0];
          switch (asset.mimeType) {
            case 'video/mp4':
            case 'video/quicktime':
              return (
                <InlineVideo caption={asset.description} src={asset.url} />
              );
            case 'image/jpeg':
            case 'image/gif':
            case 'image/png':
              return (
                <InlineImage
                  alt={asset.title}
                  caption={asset.description}
                  gatsbyImageData={asset.gatsbyImageData}
                  src={asset.url}
                />
              );
            default:
              return null;
          }
        },
        ...(renderNodeOverrides || {})
      }
    };

    return <Wrapper>{documentToReactComponents(json, renderOptions)}</Wrapper>;
  }
);
